import React, { useState, useEffect, useRef, useCallback } from 'react';
import { format, addDays, subDays, isSameDay, startOfDay, endOfDay } from 'date-fns';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, Filter, Plus, BookOpen } from 'lucide-react';
import { ClassCard, ClassCardRef } from './ClassCard';
import { Training } from '@/types/trainings';
import { useInstructorClasses } from '@/hooks/useInstructorClasses';
import { useInstructorDailyTotals } from '@/hooks/useInstructorDailyTotals';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { DailySlider } from './DailySlider';
import { ClassViewModal } from './ClassViewModal';
import { AddClassModal } from './AddClassModal';
import { ClassNotesModal } from './ClassNotesModal';
import { ClassFilterModal, ClassFilters } from './ClassFilterModal';
import { AvailabilitySlotCard } from './AvailabilitySlotCard';
import { AgendaConfigurationModal } from './AgendaConfigurationModal';
import { useCentralizedSlots } from '@/hooks/useCentralizedSlots';
import { useAgendaConfig } from '@/hooks/useAgendaConfig';

import settingsIcon from '@/assets/icons/settings.svg';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateTrainingStatus, useTrainingsWithNotes, useClassesWithNotes } from '@/hooks/useTrainings';
import { supabase } from '@/integrations/supabase/client';

interface DailyViewProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  trainings: Training[];
  onTrainingClick: (training: Training) => void;
  onAddClass: () => void;
  onConfigureAgenda: () => void;
  onEditClass?: (training: Training) => void;
  onClassDeleted?: (classId: string) => void;
}





export const DailyView: React.FC<DailyViewProps> = ({
  selectedDate,
  onDateChange,
  trainings,
  onTrainingClick,
  onAddClass,
  onConfigureAgenda,
  onEditClass,
  onClassDeleted,
}) => {
  const { user } = useAuth();
  
  // Only show if user is an instructor
  if (!user?.id) {
    return (
      <div className="text-center py-8">
        <p className="text-white/60">Please log in to view your classes.</p>
      </div>
    );
  }
  
  const [selectedClass, setSelectedClass] = useState<Training | null>(null);
  const [isClassViewModalOpen, setIsClassViewModalOpen] = useState(false);
  const [isAddClassModalOpen, setIsAddClassModalOpen] = useState(false);
  const [isEditClassModalOpen, setIsEditClassModalOpen] = useState(false);
  const [selectedClassForEdit, setSelectedClassForEdit] = useState<Training | null>(null);
  const [isClassNotesModalOpen, setIsClassNotesModalOpen] = useState(false);
  const [selectedClassForNotes, setSelectedClassForNotes] = useState<Training | null>(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isAgendaConfigModalOpen, setIsAgendaConfigModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeFilters, setActiveFilters] = useState<ClassFilters>({
    types: [],
    modalities: [],
    startDate: null,
    endDate: null,
    statuses: [],
    techniques: [],
    categories: [],
    students: []
  });
  const [showFixedButton, setShowFixedButton] = useState(false);
  const [lastCreatedClassId, setLastCreatedClassId] = useState<string | null>(null);
  const [notesRefreshKey, setNotesRefreshKey] = useState(0);
  const [isUpdatingNotes, setIsUpdatingNotes] = useState(false);
  const actionBarRef = useRef<HTMLDivElement>(null);
  const classCardRefs = useRef<Map<string, ClassCardRef>>(new Map());
  const queryClient = useQueryClient();

  // Use instructor classes hook
  const { classes, isLoading } = useInstructorClasses(selectedDate);

  // Get daily totals for the slider
  const { dailyTotals } = useInstructorDailyTotals({
    instructorId: user?.id || '',
    selectedDate,
  });

  // Get notes data for classes
  const { data: classesNotesData } = useClassesWithNotes({
    month: selectedDate.getMonth() + 1,
    year: selectedDate.getFullYear(),
  });

  // Extract notesData from the response
  const notesDataForClasses = classesNotesData?.notesData || {};

  // Move useI18n here after the other hooks
  const { t } = useI18n();

  // Filter classes based on active filters
  const filteredClasses = classes.filter(classItem => {
    // Filter by class type
    if (activeFilters.types.length > 0) {
      if (!classItem.type || !activeFilters.types.includes(classItem.type)) {
        return false;
      }
    }

    // Filter by modality
    if (activeFilters.modalities.length > 0) {
      if (!activeFilters.modalities.includes(classItem.modality)) {
        return false;
      }
    }

    // Filter by category
    if (activeFilters.categories.length > 0) {
      if (!classItem.category || !activeFilters.categories.includes(classItem.category)) {
        return false;
      }
    }

    // Filter by status
    if (activeFilters.statuses.length > 0) {
      if (!activeFilters.statuses.includes(classItem.status)) {
        return false;
      }
    }

    // Filter by date range
    if (activeFilters.startDate) {
      const classDate = new Date(classItem.start_time);
      if (classDate < activeFilters.startDate) {
        return false;
      }
    }

    if (activeFilters.endDate) {
      const classDate = new Date(classItem.start_time);
      if (classDate > activeFilters.endDate) {
        return false;
      }
    }

    return true;
  });

  // Fetch availability slots
  const { config: agendaConfig, isLoading: agendaLoading } = useAgendaConfig();
  
  // Use default config if agendaConfig is not yet loaded or is an empty array
  const effectiveConfig = (agendaConfig && typeof agendaConfig === 'object' && !Array.isArray(agendaConfig)) ? agendaConfig : {
    instructor_id: user?.id || '',
    working_days: ['mon', 'tue', 'wed', 'thu', 'fri'],
    working_hours: {
      mon: { start: '08:00', end: '18:00' },
      tue: { start: '08:00', end: '18:00' },
      wed: { start: '08:00', end: '18:00' },
      thu: { start: '08:00', end: '18:00' },
      fri: { start: '08:00', end: '18:00' }
    },
    default_duration: 60,
    is_public: false
  };
  
  const { slots: availabilitySlots } = useCentralizedSlots({
    instructorId: user?.id || '',
    selectedDate,
    classes,
    trainings
  });
  
  const slotsLoading = agendaLoading;

  const totalClasses = filteredClasses.length;

  // Handle scroll for fixed button
  useEffect(() => {
    const handleScroll = () => {
      if (actionBarRef.current) {
        const rect = actionBarRef.current.getBoundingClientRect();
        setShowFixedButton(rect.top < 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClassClick = (classData: Training) => {
    setSelectedClass(classData);
    setIsClassViewModalOpen(true);
  };

  const handleOpenNotesModal = (classData: Training) => {
    setSelectedClassForNotes(classData);
    setIsClassNotesModalOpen(true);
    setIsUpdatingNotes(true);
  };

  const handleNotesSaved = async () => {
    // Force invalidate and refetch classes to get updated data
    await queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
    await queryClient.invalidateQueries({ queryKey: ['classes-with-notes'] });
    await queryClient.invalidateQueries({ queryKey: ['instructor-daily-totals'] });
    await queryClient.invalidateQueries({ queryKey: ['journey-highlights'] });
    
    // Increment refresh key to force re-render of ClassCards
    setNotesRefreshKey(prev => prev + 1);
    setIsUpdatingNotes(false);
    
    // Close the view modal to prevent it from reopening
    setIsClassViewModalOpen(false);
    setSelectedClass(null);
    
    // Add a small delay to prevent immediate reopening
    setTimeout(() => {
      // This ensures the modal state is properly reset
    }, 100);
  };

  const updateStatusMutation = useUpdateTrainingStatus();

  const handleStatusUpdate = useCallback(async (trainingId: string, status: 'Completed' | 'Missed') => {
    try {
      await updateStatusMutation.mutateAsync({ training_id: trainingId, status });
      
      // Invalidate and refetch classes to get updated data
      await queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
      await queryClient.invalidateQueries({ queryKey: ['instructor-daily-totals'] });
      await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
      await queryClient.invalidateQueries({ queryKey: ['trainings'] });
      
      // Set the last updated class ID for scroll and animation
      setLastCreatedClassId(trainingId);
      
      // Trigger animation and scroll after a delay to allow the UI to update
      setTimeout(() => {
        const cardRef = classCardRefs.current.get(trainingId);
        if (cardRef) {
          cardRef.triggerStatusUpdateAnimation();
          cardRef.scrollIntoView();
        }
      }, 100);
      
      return Promise.resolve();
    } catch (error) {
      console.error('Error updating class status:', error);
      return Promise.reject(error);
    }
  }, [updateStatusMutation, queryClient]);

  const hasActiveFilters = () => {
    return (
      activeFilters.types.length > 0 ||
      activeFilters.modalities.length > 0 ||
      activeFilters.startDate !== null ||
      activeFilters.endDate !== null ||
      activeFilters.statuses.length > 0 ||
      activeFilters.techniques.length > 0 ||
      activeFilters.categories.length > 0
    );
  };

  const handleOpenFilterModal = () => {
    setIsFilterModalOpen(true);
  };

  const handleApplyFilters = (filters: ClassFilters) => {
    setActiveFilters(filters);
  };

  const handleClearFilters = () => {
    const emptyFilters: ClassFilters = {
      types: [],
      modalities: [],
      startDate: null,
      endDate: null,
      statuses: [],
      techniques: [],
      categories: [],
      students: []
    };
    setActiveFilters(emptyFilters);
  };

  const handleCloseClassModal = () => {
    setIsClassViewModalOpen(false);
    setSelectedClass(null);
  };

  const handleAddClass = () => {
    setIsAddClassModalOpen(true);
  };

  const handleCloseAddClassModal = () => {
    setIsAddClassModalOpen(false);
  };

  const handleOpenEditClassModal = (classData: Training) => {
    setSelectedClassForEdit(classData);
    setIsEditClassModalOpen(true);
  };

  const handleCloseEditClassModal = () => {
    setIsEditClassModalOpen(false);
    setSelectedClassForEdit(null);
  };

  const handleClassUpdated = async () => {
    // Force invalidate and refetch classes to get updated data
    await queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
    await queryClient.invalidateQueries({ queryKey: ['instructor-daily-totals'] });
    await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
    await queryClient.invalidateQueries({ queryKey: ['trainings'] });
    await queryClient.invalidateQueries({ queryKey: ['classes-with-notes'] });
    
    // If the modal is open, refresh the selected class data
    if (isClassViewModalOpen && selectedClass) {
      // Force a re-render by updating the selectedClass
      const updatedClass = await queryClient.fetchQuery({
        queryKey: ['instructor-classes', selectedClass.id],
        queryFn: async () => {
          // Fetch the updated class data
          const { data, error } = await supabase
            .from('trainings')
            .select('*')
            .eq('id', selectedClass.id)
            .eq('is_class', true)
            .single();
          
          if (error) throw error;
          return data;
        }
      });
      
      if (updatedClass) {
        setSelectedClass(updatedClass);
      }
    }
    
    // Increment refresh key to force ClassViewModal to reload students
    setRefreshKey(prev => prev + 1);
    
    // Close the edit modal
    setIsEditClassModalOpen(false);
    setSelectedClassForEdit(null);
  };

  const handleClassCreated = async (classData: any) => {
    // Force invalidate and refetch instructor classes to get the new class
    await queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
    await queryClient.invalidateQueries({ queryKey: ['instructor-daily-totals'] });
    
    // Also invalidate regular trainings since classes are stored in the same table
    await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
    await queryClient.invalidateQueries({ queryKey: ['trainings'] });
    await queryClient.invalidateQueries({ queryKey: ['monthly-totals'] });
    await queryClient.invalidateQueries({ queryKey: ['classes-with-notes'] });
    
    // Force refetch the specific queries
    await queryClient.refetchQueries({ queryKey: ['instructor-classes'] });
    await queryClient.refetchQueries({ queryKey: ['classes-with-notes'] });
    
    // Also invalidate the specific date query
    await queryClient.invalidateQueries({ 
      queryKey: ['instructor-classes', user?.id, format(selectedDate, 'yyyy-MM-dd')] 
    });
    
    // Set the last created class ID for scroll
    setLastCreatedClassId(classData.id);
    
    // Trigger scroll after a delay to allow the UI to update
    setTimeout(() => {
      const cardRef = classCardRefs.current.get(classData.id);
      if (cardRef) {
        cardRef.triggerStatusUpdateAnimation();
        cardRef.scrollIntoView();
      }
    }, 100);
    
    // Close the modal
    setIsAddClassModalOpen(false);
  };

  return (
    <div className="space-y-4">
      {/* Action Bar - Moved to top */}
      <div ref={actionBarRef} className="flex items-center gap-4 mb-6">
        <Button 
          variant="outline" 
          onClick={handleAddClass}
          className="flex-1 border-white text-white hover:bg-white/10 rounded-full font-semibold bg-transparent"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('calendar.add_class')}
        </Button>
        
        <div className="flex items-center gap-0 ml-auto">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleOpenFilterModal}
            className={`text-white hover:bg-white/10 ${
              hasActiveFilters() ? 'bg-white/10' : ''
            }`}
          >
            <Filter className="h-4 w-4" />
            {hasActiveFilters() && <span className="ml-0.5 text-xs font-bold">•</span>}
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsAgendaConfigModalOpen(true)}
            className="text-white hover:bg-white/10"
          >
            <img src={settingsIcon} alt="Settings" className="w-4 h-4" style={{ filter: 'brightness(0) invert(1)' }} />
          </Button>
        </div>
      </div>

      {/* Daily Slider - Moved after Action Bar */}
      <DailySlider
        selectedDate={selectedDate}
        onDateSelect={onDateChange}
        dailyTotals={dailyTotals}
      />

      {/* Fixed Action Bar */}
      <div 
        className={`fixed top-20 left-0 right-0 z-40 px-5 transition-all duration-300 ${
          showFixedButton 
            ? 'translate-y-0 opacity-100' 
            : '-translate-y-full opacity-0 pointer-events-none'
        }`}
      >
        <div className="max-w-md mx-auto space-y-3">
          {/* Add Class Button */}
          <Button 
            variant="outline"
            onClick={handleAddClass}
            className="w-full border-white text-white hover:bg-white/10 rounded-full font-semibold bg-[#281B1B]/90 backdrop-blur-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('calendar.add_class')}
          </Button>
        </div>
      </div>

      {/* Filter Status */}
      {hasActiveFilters() && (
        <div className="mb-6 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center justify-between text-sm">
            <span className="text-white">
              Showing {filteredClasses.length} of {classes.length} classes
            </span>
            <button 
              onClick={handleClearFilters}
              className="text-white hover:text-white/80 text-xs font-medium underline"
            >
              Clear filters
            </button>
          </div>
        </div>
      )}

            {/* Classes and Availability Slots List */}
      <div className="space-y-3">
        {/* Render in specific order: slot → class → slot → class → slot */}
        {(() => {
          // Create ordered timeline dynamically based on actual slots and classes
          const orderedItems: Array<{
            type: 'class' | 'slot';
            data: any;
            key: string;
            startTime: string; // For sorting
          }> = [];

          // Add all slots
          availabilitySlots.forEach((slot, index) => {
            orderedItems.push({
              type: 'slot',
              data: slot,
              key: `slot-${index + 1}`,
              startTime: slot.startTime
            });
          });

          // Add all classes
          filteredClasses.forEach((classItem, index) => {
            orderedItems.push({
              type: 'class',
              data: classItem,
              key: `class-${classItem.id}`,
              startTime: new Date(classItem.start_time).toTimeString().slice(0, 5)
            });
          });

          // Sort all items chronologically by start time
          orderedItems.sort((a, b) => {
            const timeA = a.startTime;
            const timeB = b.startTime;
            return timeA.localeCompare(timeB);
          });

          return orderedItems.map((item) => {
            if (item.type === 'class') {
              return (
                <ClassCard
                  key={item.key}
                  ref={(ref) => {
                    if (ref) {
                      classCardRefs.current.set(item.data.id, ref);
                    } else {
                      classCardRefs.current.delete(item.data.id);
                    }
                  }}
                  classData={item.data}
                  onViewTraining={handleClassClick}
                  onStatusUpdate={handleStatusUpdate}
                  onNotesUpdate={(trainingId: string) => {
                    // Only open notes modal if it's not already open
                    if (!isClassNotesModalOpen) {
                      const classData = filteredClasses.find(c => c.id === trainingId);
                      if (classData) {
                        handleOpenNotesModal(classData);
                      }
                    }
                  }}
                  isCurrentDay={false}
                  notesData={notesDataForClasses}
                  notesRefreshKey={notesRefreshKey}
                  isUpdatingNotes={isUpdatingNotes}
                />
              );
            } else {
              return (
                <AvailabilitySlotCard
                  key={item.key}
                  {...item.data}
                />
              );
            }
          });
        })()}
      </div>

      {/* Loading State */}
      {(isLoading || slotsLoading) && (
        <div className="text-center py-8">
          <p className="text-white/60">Loading classes and availability...</p>
        </div>
      )}

      {/* No Classes Message */}
      {!isLoading && !slotsLoading && totalClasses === 0 && availabilitySlots.length === 0 && (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          {/* Empty State Icon */}
          <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
            <svg 
              className="w-10 h-10 text-white/40" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={1.5} 
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" 
              />
            </svg>
          </div>
          
          {/* Empty State Title */}
          <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
            {t('calendar.start_teaching_add_first_class')}
          </h3>
          
          {/* Empty State Subtitle */}
          <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
            {t('calendar.schedule_sessions_help_students')}
          </p>
          
          {/* Call to Action Button */}
          <Button 
            variant="outline"
            onClick={handleAddClass}
            className="mt-8 border-white text-white hover:bg-white/10 rounded-full font-semibold bg-transparent px-8 py-3"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('calendar.add_your_first_class')}
          </Button>
        </div>
      )}

      {/* Availability Slots Only Message */}
      {!isLoading && !slotsLoading && totalClasses === 0 && availabilitySlots.length > 0 && (
        <div className="flex flex-col items-center justify-center min-h-[40vh] text-center px-4 mb-8">
          <h3 className="text-lg font-semibold text-white mb-2 font-roboto">
            {t('calendar.available_time_slots')}
          </h3>
          <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
            {(() => {
              const totalSlotCount = availabilitySlots.reduce((total, slot) => total + slot.slotCount, 0);
              return totalSlotCount === 1 
                ? t('calendar.you_have_time_slots_available_singular')
                : t('calendar.you_have_time_slots_available_plural', { count: totalSlotCount });
            })()}
          </p>
        </div>
      )}

      {/* Class View Modal */}
      {selectedClass && (
        <ClassViewModal
          isOpen={isClassViewModalOpen}
          onClose={handleCloseClassModal}
          classData={selectedClass}
          notesData={notesDataForClasses}
          onStatusUpdate={handleStatusUpdate}
          onOpenNotesModal={handleOpenNotesModal}
          onEditClass={handleOpenEditClassModal}
          onClassDeleted={onClassDeleted}
          refreshKey={refreshKey}
        />
      )}

      {/* Add/Edit Class Modal */}
      <AddClassModal
        isOpen={isAddClassModalOpen || isEditClassModalOpen}
        onClose={() => {
          setIsAddClassModalOpen(false);
          setIsEditClassModalOpen(false);
          setSelectedClassForEdit(null);
        }}
        onClassCreated={handleClassCreated}
        mode={isEditClassModalOpen ? 'edit' : 'create'}
        classToEdit={selectedClassForEdit}
        onClassUpdated={handleClassUpdated}
      />

      {/* Class Notes Modal */}
      <ClassNotesModal
        isOpen={isClassNotesModalOpen}
        onClose={() => {
          setIsClassNotesModalOpen(false);
          setSelectedClassForNotes(null);
          setIsUpdatingNotes(false); // Reset loading state when modal is closed
        }}
        classData={selectedClassForNotes}
        notesData={notesDataForClasses}
        onDataSaved={handleNotesSaved}
      />

      {/* Class Filter Modal */}
      <ClassFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        currentFilters={activeFilters}
        hasActiveFilters={hasActiveFilters()}
      />

      {/* Agenda Configuration Modal */}
      <AgendaConfigurationModal
        isOpen={isAgendaConfigModalOpen}
        onClose={() => setIsAgendaConfigModalOpen(false)}
        onConfigSaved={() => {
          // Refresh agenda config to update slots
          queryClient.invalidateQueries({ queryKey: ['agenda-config'] });
        }}
      />
    </div>
  );
};
